/**
 * ParentHomeScreen — primary adapter for the parent home tab (`parent-home`).
 *
 * Thin wire: data + view-model + state → DS `<ParentHomePage>` + overlays.
 */
import { useSelector } from "react-redux"

import {
    ConfirmDialog,
    ExtraHoursFlow,
    ParentHomePage,
    RecoverAbsenceFlow,
    SendTutorMessageModal,
    StayWithTutorFlow,
} from "../../../../../../design-system"
import {
    homeAssessmentsLoading,
    parentHomeViewModel,
    selectAssessmentsRequesting,
    selectChaptersRequesting,
} from "../../../../view-models/parent/ParentHome/parentHomeViewModel"
import { type UILiveSession } from "../../../types"
import { useExtraHoursFlow } from "../../../hooks/useExtraHoursFlow"
import { useRecoverAbsenceFlow } from "../../../hooks/useRecoverAbsenceFlow"
import { useReferralEntry } from "../../../hooks/useReferralEntry"
import { useStageOfferEntry } from "../../../hooks/useStageOfferEntry"
import { useStayWithTutorFlow } from "../../../hooks/useStayWithTutorFlow"
import { PARENT_TAB_BAR_HEIGHT } from "../ParentTabBar"
import { ParentSessionGestures } from "../shared/ParentSessionGestures"
import { useParentHomeData } from "./useParentHomeData"
import { useParentHomeState } from "./useParentHomeState"
import { useParentRequests } from "./useParentRequests"

export type ParentHomeScreenProps = {
    selectedUser?: { userId?: string | number } | null
    /** La déconnexion de la coquille (`ParentMainLayout`) : « Retour à la sélection de compte ». */
    startLogout?: () => void
    /** Famille sans abonnement actif : la tuile reste en place, grisée (garde `isSubscribed`). */
    addOneOffLessonDisabled?: boolean
    /** Le bloc « heures supplémentaires » est éteint dans l'espace de l'élève : pas de tuile du tout. */
    addOneOffLessonHidden?: boolean
    /**
     * Quitte l'espace parent pour celui de l'enfant, sur cette séance. Tenu par l'enveloppe
     * legacy qui monte cet écran (`ParentHomeWithExtraHours`) : la bascule de compte et la
     * navigation différée vivent dans `src/common/**`, que `ports/**` n'importe pas. ABSENT =
     * pas de ligne « Rejoindre » dans la feuille d'actions.
     */
    onJoinAsChild?: (session: UILiveSession) => void
    /**
     * Le choix « Garder son tuteur » qu'un lien demande d'ouvrir. L'enveloppe legacy le lit
     * dans la route et bascule d'abord sur l'enfant concerné ; `onTutorSlotMoveLinkOpened`
     * lui dit que la feuille est ouverte.
     */
    openTutorSlotMoveId?: string | number | null
    onTutorSlotMoveLinkOpened?: () => void
}

export function ParentHomeScreen(props: ParentHomeScreenProps) {
    const selectedUserId = props.selectedUser?.userId ?? null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vm = useSelector((state: any) => parentHomeViewModel(state, selectedUserId))
    const assessmentsRequesting = useSelector(selectAssessmentsRequesting)
    const chaptersLoading = useSelector(selectChaptersRequesting)
    const { refreshPricing, refreshAssessments, refreshLives } = useParentHomeData({
        selectedUserId,
        studentEmail: vm.studentEmail,
        studentId: vm.studentId,
        identityReady: vm.identityReady,
    })
    const extraHours = useExtraHoursFlow({
        childFirstname: vm.child?.firstname,
        snackbarBottomOffset: PARENT_TAB_BAR_HEIGHT,
    })
    const recovery = useRecoverAbsenceFlow({
        childFirstname: vm.child?.firstname,
        studentEmail: vm.studentEmail,
        onSuccess: refreshLives,
        snackbarBottomOffset: PARENT_TAB_BAR_HEIGHT,
    })
    const tutorChange = useStayWithTutorFlow({
        childFirstname: vm.child?.firstname,
        studentId: vm.studentId,
        onChanged: refreshLives,
        snackbarBottomOffset: PARENT_TAB_BAR_HEIGHT,
        openMoveId: props.openTutorSlotMoveId,
        onOpenedFromLink: props.onTutorSlotMoveLinkOpened,
    })
    const referral = useReferralEntry("home_banner")
    const requests = useParentRequests(vm.studentId)
    const stageOfferEntry = useStageOfferEntry(vm.stageOffer, vm.studentId, !vm.loading && vm.error == null)
    const {
        actions,
        gestures,
        joinAsChildFirstname,
        assessmentModalVisible,
        assessmentModalTitle,
        isEditingAssessment,
        createAssessmentData,
        chapters,
        mediaProps,
        hideAssessmentModalForCamera,
        analyzeAssessmentLoading,
        submitAssessmentDisabled,
        submitAssessmentLoading,
        messageModal,
        confirm,
    } = useParentHomeState({
        studentEmail: vm.studentEmail,
        studentId: vm.studentId,
        identityReady: vm.identityReady,
        lastReportHash: vm.lastReport?.hash,
        childFirstname: vm.child?.firstname,
        nextSessions: vm.nextSessions,
        sessionsLoading: vm.loading,
        tutors: vm.tutors,
        assessments: vm.assessments,
        catalogChapters: vm.chapters,
        hasShellError: vm.error != null,
        startLogout: props.startLogout,
        onRefreshPricing: refreshPricing,
        onAssessmentsChanged: refreshAssessments,
        onAddOneOffLesson: extraHours.open,
        onLivesChanged: refreshLives,
        onJoinAsChild: props.onJoinAsChild,
        paymentUrl: vm.paymentUrl,
        onPlanRecovery: recovery.open,
        onChooseTutorChange: tutorChange.open,
    })

    return (
        <>
            <ParentHomePage
                child={vm.child ?? undefined}
                todos={vm.todos}
                requests={requests.requests}
                onOpenRequest={requests.openRequest}
                nextSessions={vm.nextSessions}
                upcomingSessions={vm.upcomingSessions}
                hasMoreLives={vm.hasMoreLives}
                lastReport={vm.lastReport}
                revisionPrograms={vm.revisionPrograms}
                assessments={vm.assessments}
                assessmentsLoading={homeAssessmentsLoading(vm, assessmentsRequesting)}
                loading={vm.loading}
                error={vm.error}
                onRetry={actions.onRetry}
                onSessionAction={actions.onSessionAction}
                joinAsChildFirstname={joinAsChildFirstname}
                onTodoPress={actions.onTodoPress}
                onOpenLastReport={actions.onOpenLastReport}
                onAddOneOffLesson={actions.onAddOneOffLesson}
                addOneOffLessonDisabled={props.addOneOffLessonDisabled}
                addOneOffLessonHidden={props.addOneOffLessonHidden}
                onMoveSession={actions.onMoveSession}
                onReportLate={actions.onReportLate}
                onMessageTutor={actions.onMessageTutor}
                hasUpcomingSession={vm.hasUpcomingSession}
                onSeeMoreLives={actions.onSeeMoreLives}
                onOpenRevisionProgram={actions.onOpenRevisionProgram}
                onEditAssessment={actions.onEditAssessment}
                onDeleteAssessment={actions.onDeleteAssessment}
                onAssessmentDetails={actions.onAssessmentDetails}
                assessmentModalVisible={assessmentModalVisible}
                onOpenAssessmentModal={actions.onOpenAssessmentModal}
                onCloseAssessmentModal={actions.onCloseAssessmentModal}
                assessmentModalTitle={assessmentModalTitle}
                isEditingAssessment={isEditingAssessment}
                createAssessmentData={createAssessmentData}
                onCreateAssessmentDataChange={actions.onCreateAssessmentDataChange}
                subjects={vm.subjects}
                chapters={chapters}
                chaptersLoading={chaptersLoading}
                mediaProps={mediaProps}
                hideAssessmentModalForCamera={hideAssessmentModalForCamera}
                onAnalyzeAssessment={actions.onAnalyzeAssessment}
                analyzeAssessmentLoading={analyzeAssessmentLoading}
                onSubmitAssessment={actions.onSubmitAssessment}
                submitAssessmentDisabled={submitAssessmentDisabled}
                submitAssessmentLoading={submitAssessmentLoading}
                onBlockedSessionPress={actions.onBlockedSessionPress}
                stageOffer={vm.stageOffer}
                onOpenStageOffer={stageOfferEntry.onOpenStageOffer}
                onOpenReferral={referral.onOpenReferral}
                referralRewardLabel={referral.rewardLabel}
            />
            {/* Une seule fente d'accusé : l'issue d'un parcours fermé pendant son envoi. */}
            <ParentSessionGestures
                {...gestures}
                screenNotice={[extraHours.notice, recovery.notice].find((n) => n.visible) ?? tutorChange.notice}
            />
            <ExtraHoursFlow {...extraHours.flowProps} />
            <RecoverAbsenceFlow {...recovery.flowProps} />
            <StayWithTutorFlow {...tutorChange.flowProps} />
            <SendTutorMessageModal {...messageModal} />
            <ConfirmDialog
                visible={confirm != null}
                title={confirm?.title ?? ""}
                message={confirm?.message}
                confirmLabel={confirm?.confirmLabel}
                cancelLabel={confirm?.cancelLabel}
                danger={confirm?.danger ?? false}
                onConfirm={actions.onConfirmDialog}
                onCancel={actions.onCancelDialog}
                onDismiss={actions.onDismissDialog}
            />
        </>
    )
}

export default ParentHomeScreen
